import React from "react";
import DashboardBarChart from "../../common/DashboardBarChart";
import { BRAND, NEUTRAL, getPaletteColor } from "../../../utils/chartTheme";
import { formatCount } from "../../../utils/numberFormat";

const getConditionColor = (condition) => {
  switch (condition) {
    case "Excellent Condition":
      return BRAND.teal;
    case "Good Condition":
      return BRAND.tealLight;
    case "Fair Condition":
      return BRAND.gold;
    case "Poor Condition":
      return getPaletteColor(4);
    default:
      return NEUTRAL.muted;
  }
};

const calculateConditionScore = (conditionData) => {
  if (!conditionData || !conditionData.length) return 0;

  const weights = {
    "Excellent Condition": 100,
    "Good Condition": 75,
    "Fair Condition": 50,
    "Poor Condition": 25,
  };

  const totalCount = conditionData.reduce((sum, item) => sum + item.count, 0);
  if (totalCount === 0) return 0;

  const weightedSum = conditionData.reduce((sum, item) => {
    return sum + item.count * (weights[item.name] || 50);
  }, 0);

  return Math.round(weightedSum / totalCount);
};

const ConditionAssessment = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="bg-white rounded-lg p-3 shadow-sm animate-pulse w-full">
        <div className="h-3 bg-gray-300 rounded w-1/4 mb-2"></div>
        <div className="h-48 bg-gray-200 rounded w-full"></div>
      </div>
    );
  }

  const formatPercentage = (value) => {
    if (!value) return "0%";
    return `${parseFloat(value).toFixed(1)}%`;
  };
  const conditionData = data?.conditionAssessment
    ? Object.entries(data.conditionAssessment).map(([condition, details]) => ({
        name: condition,
        count: details.count || 0,
        percentage: parseFloat(details.percentage) || 0,
        color: getConditionColor(condition),
      }))
    : [];
  const barChartData = [...conditionData].sort((a, b) => b.count - a.count);

  const totalAssessed = conditionData.reduce(
    (sum, item) => sum + item.count,
    0
  );

  return (
    <div className="bg-white rounded-lg shadow-sm w-full ">
      <div className="mb-4">
        <h3 className="text-lg font-bold text-gray-800 text-center text-balance">
          Condition Assessment
        </h3>
        <div className="text-sm text-gray-600 text-center">
          Total Assessed: <span className="font-bold">{totalAssessed}</span>
        </div>
      </div>
      <div className="h-64 mb-6">
        <DashboardBarChart
          items={barChartData.map((item) => ({
            name: item.name,
            count: item.count,
            color: item.color,
          }))}
          unit="implementations"
        />
      </div>
      <div className="grid grid-cols-4 gap-3 mb-6">
        {conditionData.map((condition, index) => (
          <div
            key={index}
            className="flex flex-col items-center p-3 border border-gray-200 rounded-lg bg-gray-50"
          >
            <div className="flex items-center mb-2">
              <div
                className="w-3 h-3 rounded-full mr-2"
                style={{ backgroundColor: condition.color }}
              ></div>
              <span className="text-base font-bold text-gray-900">
                {formatCount(condition.count)}
              </span>
            </div>
            <span className="text-sm font-medium text-gray-700 text-center">
              {condition.name.split(" ")[0]}
            </span>
            <span className="text-sm text-gray-600">
              {condition.percentage.toFixed(1)}%
            </span>
          </div>
        ))}
      </div>
      <div className="mt-6 pt-4 border-t border-gray-300">
        <div className="text-center">
          <p className="text-base font-medium text-gray-700 mb-2">
            Overall Condition Score
          </p>
          <div className="flex items-center justify-center">
            <div className="relative w-20 h-20">
              <svg className="w-full h-full" viewBox="0 0 100 100">
                <circle
                  cx="50"
                  cy="50"
                  r="40"
                  fill="none"
                  stroke="#e5e7eb"
                  strokeWidth="10"
                />
                <circle
                  cx="50"
                  cy="50"
                  r="40"
                  fill="none"
                  stroke={BRAND.teal}
                  strokeWidth="10"
                  strokeDasharray={`${calculateConditionScore(
                    conditionData
                  )} 100`}
                  strokeDashoffset="25"
                  transform="rotate(-90 50 50)"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-lg font-bold text-gray-800">
                  {calculateConditionScore(conditionData)}%
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConditionAssessment;
